import React, { useEffect, useState } from "react";
import { useField } from "formik";
import { useFormikContext } from "formik";
import { pascalCaseToCamelCase } from "helpers/pascalCaseToCamelCase";
import { FormField } from "@avamae/formbuilder";
import {
  ActionTypes,
  selectFormReadOnlyFields,
  selectRelatedFormActionType,
} from "reducers/listPage";
import { useSelector } from "react-redux";
import { AccessTypeCodes } from "types/AccessTypeCodes";



type AccessOptions = {
  label: string;
  value: number;
  internalCode: string;
};
export interface EditSingleSelectRadioProps {
  name: string;
  label: string;
  readOnly?: boolean;
  dataSource: string;
  editDisable?: boolean;
}

const EditSingleSelectRadio = function<T>({
  name,
  dataSource,
  label,
  readOnly = false,
  editDisable = false,
}: EditSingleSelectRadioProps) {
  const [options, setOptions] = useState<AccessOptions[] | null>([]);
  const [fieldFormik, meta] = useField<any>(name as string);

  // Fields that can't be edited in create/edit forms:
  const formReadOnlyFields = useSelector(selectFormReadOnlyFields);
  const editDisabled =
    name && formReadOnlyFields && formReadOnlyFields.hasOwnProperty(name);
  const editDisabledLabel =
    editDisabled &&
    formReadOnlyFields &&
    formReadOnlyFields.hasOwnProperty(name)
      ? formReadOnlyFields[name]
      : null;

  const relatedFormActionType = useSelector(selectRelatedFormActionType);

  const formik = useFormikContext<any>();
  if (typeof dataSource === "string") {
    dataSource = pascalCaseToCamelCase(dataSource);
  }

  useEffect(() => {
    if (
      formik &&
      formik.values &&
      typeof formik.values === "object" &&
      dataSource &&
      formik.values.hasOwnProperty(dataSource) &&
      Array.isArray(formik.values[dataSource as keyof typeof formik.values])
    ) {
      setOptions([...formik.values[dataSource as keyof typeof formik.values]]);
    }
  }, [formik.values]);

  //set initial field value
  useEffect(() => {
    if (formReadOnlyFields) {
      const id = options?.find(
        option =>
          option.internalCode === formReadOnlyFields.contactAccessTypesId
      )?.value;
      formik.setFieldValue(name, id);
    }
    if (
      formik.values[name as keyof typeof formik.values] === null ||
      formik.values[name as keyof typeof formik.values] === undefined
    ) {
      const id = options?.find(
        option => option.internalCode === AccessTypeCodes.GLOBAL
      )?.value;

      formik.setFieldValue(name, id);
    }
  }, [options]);

  return (
    <div className="FormBox FormSideBySide">
      <div className={`FormLabel ${editDisabled ? "EditDisabled" : ""}`}>
        <label>{label}</label>
      </div>
      {editDisabled && (
        <div className="EditDisabledMessage">
          {relatedFormActionType === ActionTypes.CREATE
            ? `${editDisabledLabel ?? "Field"} will be chosen after
          submitting`
            : `The related ${editDisabledLabel ??
                "Field"} will be automatically chosen after submitting`}
        </div>
      )}

      <div
        className={`ConditionalField RadioButtons ${
          editDisabled || editDisable ? "EditDisabled" : ""
        }`}
      >
        {options?.map((option, idx) => {
          return (
            <div
              key={`${idx}- ${option.label}`}
              className={`Radio ${
                fieldFormik.value === option.value ? "Checked" : ""
              }`}
              role="checkbox"
              aria-checked={fieldFormik.value === option.value}
              onClick={() => {
                formik.setFieldValue(name, option.value);
              }}
            >
              <input
                id={`${name} + ${idx}`}
                type="radio"
                name={option.label}
                value={option.value}
                checked={fieldFormik.value === option.value}
                onChange={() => {}} // Prevent console error about providing a checked attribute but no onChange prop. The onChange is the onClick on the div that wraps this.
              />
              <label className="CustomInput" htmlFor={`${name} + ${idx}`}>
                {option.label}
              </label>
            </div>
          );
        })}
        {meta.touched && meta.error && (
          <div className="error">{meta.error}</div>
        )}
      </div>
    </div>
  );
};
export { EditSingleSelectRadio };

export const editSingleSelectRadio = (readOnly: boolean = false) =>
  function editSingleSelectRadio<T>(
    _key: React.ReactText,
    field: FormField<T>
  ) {
    return (
      <EditSingleSelectRadio
        name={field.name as string}
        label={field.label}
        readOnly={readOnly}
        dataSource={field.dataSource as string}
      />
    );
  };
